import React, { useState, useEffect, Suspense } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import DashboardLayout from "../components/DashboardLayout";
import DashboardCard from "../components/DashboardCard";
import { ArrowLeft, Clock, Calendar, User } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { Badge } from "@/components/ui/badge";
import { menuItems } from "../components/DashboardSidebar";
import { getBlogPostById } from "../content";
import { BlogPost as BlogPostType } from "../components/BlogCard";
import matter from "gray-matter";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import ImageLoader from "../components/ImageLoader";
import NewsletterSection from "../components/newsletter/NewsletterSection";

const stripFrontmatter = (content: string): string => {
  if (!content) return "";

  try {
    const { content: strippedContent } = matter(content);
    return strippedContent.trim();
  } catch (error) {
    console.error("Failed to strip frontmatter:", error);

    let cleaned = content;

    cleaned = cleaned.replace(/^---[\s\S]*?---/m, "");

    cleaned = cleaned.replace(
      /^(id|title|excerpt|date|category|readTime|imageUrl):.*$/gm,
      ""
    );

    cleaned = cleaned.replace(/^\s*[\r\n]+/, "");

    return cleaned.trim();
  }
};

const BlogPost = () => {
  const { postId } = useParams<{ postId: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<
    (BlogPostType & { MDXContent?: React.ComponentType }) | null
  >(null);
  const [loading, setLoading] = useState(true);
  const [firstImageLoaded, setFirstImageLoaded] = useState(false);

  useEffect(() => {
    const loadPost = async () => {
      if (!postId) return;

      try {
        setLoading(true);
        const foundPost = await getBlogPostById(postId);

        if (foundPost) {
          if (foundPost.content) {
            foundPost.content = stripFrontmatter(foundPost.content);
          }
          setPost(
            foundPost as BlogPostType & { MDXContent?: React.ComponentType }
          );

          if (foundPost.imageUrl) {
            const img = new Image();
            img.src = foundPost.imageUrl;
            img.onload = () => setFirstImageLoaded(true);
          }
        } else {
          navigate("/blog");
        }
      } catch (error) {
        console.error("Failed to load blog post:", error);
        navigate("/blog");
      } finally {
        setLoading(false);
      }
    };

    loadPost();
  }, [postId, navigate]);

  useEffect(() => {
    const blogMenuItem = menuItems.find((item) => item.label === "Blog");
    if (blogMenuItem) {
      const activeItemEvent = new CustomEvent("setActiveItem", {
        detail: "Blog",
      });
      document.dispatchEvent(activeItemEvent);
    }
  }, []);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="w-full max-w-5xl mx-auto pt-6 pb-12">
          <div className="animate-pulse">
            <div className="h-6 w-32 bg-secondary/50 rounded mb-10"></div>
            <div className="h-10 w-3/4 bg-secondary/50 rounded mb-6"></div>
            <div className="h-4 w-1/3 bg-secondary/50 rounded mb-10"></div>
            <div className="space-y-4">
              <div className="h-4 bg-secondary/50 rounded w-full"></div>
              <div className="h-4 bg-secondary/50 rounded w-5/6"></div>
              <div className="h-4 bg-secondary/50 rounded w-4/6"></div>
            </div>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  if (!post) {
    return null;
  }

  const MDXContent = post.MDXContent;

  let isFirstImage = true;

  return (
    <DashboardLayout>
      <Helmet>
        <title>{post.title} | OpenPipe</title>
        {post.excerpt && <meta name="description" content={post.excerpt} />}
      </Helmet>
      <div className="w-full max-w-3xl mx-auto pt-6 pb-12">
        <Link
          to="/blog"
          className="inline-flex items-center mb-6 md:mb-10 text-sm text-muted-foreground hover:text-primary transition-colors"
        >
          <ArrowLeft size={16} className="mr-2" />
          Back to all articles
        </Link>

        <div className="mb-6 md:mb-10">
          <Badge
            variant="outline"
            className="bg-primary/10 text-primary border-primary/20 rounded-full px-3 md:px-4 py-1 text-sm font-medium mb-4 md:mb-6"
          >
            {post.category}
          </Badge>
          <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-4 md:mb-6 leading-tight">
            {post.title}
          </h1>

          <div className="flex items-center text-sm text-muted-foreground gap-4 md:gap-6 flex-wrap">
            <div className="flex items-center">
              <User size={16} className="mr-2 text-primary" />
              {post.author}
            </div>
            <div className="flex items-center">
              <Calendar size={16} className="mr-2 text-primary" />
              {post.date}
            </div>
            <div className="flex items-center">
              <Clock size={16} className="mr-2 text-primary" />
              {post.readTime} min read
            </div>
          </div>
        </div>

        <div className="prose prose-lg dark:prose-invert max-w-none overflow-x-auto">
          {MDXContent ? (
            <div className="mdx-content">
              <Suspense
                fallback={
                  <div className="animate-pulse space-y-4">
                    <div className="h-4 bg-secondary/30 rounded w-full"></div>
                    <div className="h-4 bg-secondary/30 rounded w-5/6"></div>
                    <div className="h-4 bg-secondary/30 rounded w-4/6"></div>
                  </div>
                }
              >
                <MDXContent />
              </Suspense>
            </div>
          ) : post.content ? (
            <ReactMarkdown
              components={{
                h2: ({ node, ...props }) => (
                  <h2
                    className="text-xl md:text-2xl font-bold mt-8 md:mt-10 mb-3 md:mb-4 text-primary"
                    {...props}
                  />
                ),
                h3: ({ node, ...props }) => (
                  <h3
                    className="text-lg md:text-xl font-bold mt-6 md:mt-8 mb-2 md:mb-3 text-primary/90"
                    {...props}
                  />
                ),
                p: ({ node, children, ...props }) => {
                  const content = String(children || "");
                  if (
                    /^(id|title|excerpt|date|category|readTime|imageUrl):/.test(
                      content
                    )
                  ) {
                    return null;
                  }
                  return (
                    <p
                      className="mb-4 md:mb-6 leading-relaxed text-base"
                      {...props}
                    >
                      {children}
                    </p>
                  );
                },
                ul: ({ node, ...props }) => (
                  <ul className="md-list-disc my-4 md:my-6 pl-5" {...props} />
                ),
                ol: ({ node, ...props }) => (
                  <ol
                    className="md-list-decimal my-4 md:my-6 pl-8 md:pl-10"
                    {...props}
                  />
                ),
                li: ({ node, ...props }) => (
                  <li className="mb-2 pl-4 ml-2" {...props} />
                ),
                a: ({ node, href, ...props }) => (
                  <a
                    href={href}
                    className="blog-post-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    {...props}
                  />
                ),
                blockquote: ({ node, ...props }) => (
                  <blockquote
                    className="border-l-4 border-primary/30 pl-4 italic my-4 md:my-6"
                    {...props}
                  />
                ),

                table: ({ node, ...props }) => (
                  <div className="my-6 overflow-x-auto w-full">
                    <Table className="w-full border-collapse border border-border rounded-md">
                      {props.children}
                    </Table>
                  </div>
                ),
                thead: ({ node, ...props }) => (
                  <TableHeader className="bg-secondary/30" {...props} />
                ),
                tbody: ({ node, ...props }) => <TableBody {...props} />,
                tr: ({ node, ...props }) => (
                  <TableRow className="border-b border-border" {...props} />
                ),
                th: ({ node, ...props }) => (
                  <TableHead
                    className="px-4 py-2 text-left font-medium"
                    {...props}
                  />
                ),
                td: ({ node, ...props }) => (
                  <TableCell
                    className="px-4 py-2 border-t border-border"
                    {...props}
                  />
                ),

                code: ({ node, className, children, ...props }) => {
                  const match = /language-(\w+)/.exec(className || "");
                  return !className ? (
                    <div className="my-4 md:my-6">
                      <pre className="bg-secondary/50 p-3 md:p-4 rounded-md overflow-x-auto font-mono text-xs md:text-sm">
                        <code className="language-text" {...props}>
                          {children}
                        </code>
                      </pre>
                    </div>
                  ) : (
                    <code
                      className="bg-secondary/70 px-1.5 py-0.5 rounded text-sm font-mono"
                      {...props}
                    >
                      {children}
                    </code>
                  );
                },
                img: ({ node, src, alt, ...props }) => {
                  const isPriority = isFirstImage;
                  isFirstImage = false;

                  return (
                    <ImageLoader
                      src={src || ""}
                      alt={alt || "Blog image"}
                      className="w-full h-auto rounded-md my-6"
                      priority={isPriority}
                    />
                  );
                },
              }}
            >
              {post.content}
            </ReactMarkdown>
          ) : (
            <p className="text-muted-foreground text-center py-10">
              This content is still being written. Please check back later.
            </p>
          )}
        </div>

        <div className="text-center mt-10 md:mt-16 mb-12">
          <h3 className="text-lg md:text-xl font-medium mb-4 md:mb-6">
            Enjoy this article? Join our newsletter
          </h3>
          <div className="max-w-2xl mx-auto">
            <NewsletterSection hideHeading={true} />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default BlogPost;
